import { Predictions, useDetectionModel } from '@imaaagery/a3camera';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { buildClosestPointEdges, getPredictionMeshPoints, MeshEdge, MeshPoint } from '../constantes/loadingMaskMesh';
import { CliniqueState } from '../type';

type DebugSide = 'left' | 'right' | 'both';

type DebugRenderState = {
	displayPointIds: number[];
	edges: MeshEdge[];
	mirrorByPointId: Map<number, number>;
	centerPointIds: Set<number>;
	points: MeshPoint[];
};

const CENTER_AXIS_POINT_IDS = [10, 151, 9, 8, 168, 195, 4, 94];
const FACE_OVAL_POINT_IDS = [
	10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378, 400, 377, 152,
	148, 176, 149, 150, 136, 172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109,
];
const CLICK_RADIUS = 12;

const DebugLoadingMask = () => {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const renderStateRef = useRef<DebugRenderState | null>(null);
	const [disabledPointIds, setDisabledPointIds] = useState<Set<number>>(() => new Set());
	const [side, setSide] = useState<DebugSide>('left');
	const [activeCount, setActiveCount] = useState(0);
	const [displayCount, setDisplayCount] = useState(0);
	const [totalCount, setTotalCount] = useState(0);
	const [hoveredPoint, setHoveredPoint] = useState<{ pointId: number; x: number; y: number } | null>(null);
	const [uploadedImageUser, setUploadedImageUser] = useState<string | null>(null);
	const [uploadedPredictions, setUploadedPredictions] = useState<Predictions | null>(null);
	const [uploadStatus, setUploadStatus] = useState('');
	const { detectionModel, isModelLoaded, isModelLoading } = useDetectionModel();
	const storeImageUser = useSelector((state: CliniqueState) => state.session.imageUser);
	const storePredictions = useSelector((state: CliniqueState) => state.session.predictions);
	const cachedDebugData = getCachedDebugData();
	const imageUser = uploadedImageUser ?? storeImageUser ?? cachedDebugData.imageUser;
	const predictions = uploadedPredictions ?? storePredictions ?? cachedDebugData.predictions;

	useEffect(() => {
		if (!imageUser || !predictions) return;

		const canvas = canvasRef.current;
		if (!canvas) return;

		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		const image = new Image();
		image.src = imageUser;

		const draw = () => {
			const pixelRatio = window.devicePixelRatio || 1;
			const width = window.innerWidth;
			const height = window.innerHeight;

			canvas.width = Math.round(width * pixelRatio);
			canvas.height = Math.round(height * pixelRatio);
			canvas.style.width = `${width}px`;
			canvas.style.height = `${height}px`;

			ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
			ctx.clearRect(0, 0, width, height);

			const scale = Math.min(width / image.width, height / image.height);
			const imageWidth = image.width * scale;
			const imageHeight = image.height * scale;
			const offsetX = (width - imageWidth) / 2;
			const offsetY = (height - imageHeight) / 2;

			ctx.fillStyle = '#000';
			ctx.fillRect(0, 0, width, height);
			ctx.drawImage(image, offsetX, offsetY, imageWidth, imageHeight);

			const sourcePoints = getPredictionMeshPoints(predictions as Predictions, image.width, image.height);
			const points = sourcePoints.map((point) => ({
				x: offsetX + point.x * scale,
				y: offsetY + point.y * scale,
			}));
			const centerX = getCenterX(points);
			const centerPointIds = getCenterPointIds(points);
			const displayPointIds = getSidePointIds(points, centerX, centerPointIds, side);
			const mirrorByPointId = buildMirrorMap(points, displayPointIds, centerX, centerPointIds, side);
			const edges = buildDebugEdges(points, displayPointIds);
			const renderState = { displayPointIds, edges, mirrorByPointId, centerPointIds, points };

			renderStateRef.current = renderState;
			setActiveCount(getExportPointIds(renderState, disabledPointIds).length);
			setDisplayCount(displayPointIds.length);
			setTotalCount(points.length);
			drawMask(ctx, renderState, disabledPointIds);
		};

		image.onload = draw;
		if (image.complete) draw();
		window.addEventListener('resize', draw);

		return () => {
			image.onload = null;
			window.removeEventListener('resize', draw);
		};
	}, [disabledPointIds, imageUser, predictions, side]);

	const handleCanvasClick = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
		const renderState = renderStateRef.current;
		if (!renderState) return;

		const rect = event.currentTarget.getBoundingClientRect();
		const x = event.clientX - rect.left;
		const y = event.clientY - rect.top;
		const closestPointId = getClosestDisplayPointId(renderState, x, y);
		if (closestPointId === null) return;

		setDisabledPointIds((current) => {
			const next = new Set(current);
			next.has(closestPointId) ? next.delete(closestPointId) : next.add(closestPointId);
			return next;
		});
	}, []);

	const handleCanvasMouseMove = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
		const renderState = renderStateRef.current;
		if (!renderState) return;

		const rect = event.currentTarget.getBoundingClientRect();
		const x = event.clientX - rect.left;
		const y = event.clientY - rect.top;
		const closestPointId = getClosestDisplayPointId(renderState, x, y);

		if (closestPointId === null) {
			setHoveredPoint(null);
			return;
		}

		const point = renderState.points[closestPointId];
		setHoveredPoint({ pointId: closestPointId, x: point.x, y: point.y });
	}, []);

	const handleSave = () => {
		const renderState = renderStateRef.current;
		if (!renderState) return;

		const activeSourcePointIds = renderState.displayPointIds.filter((pointId) => !disabledPointIds.has(pointId));
		const activePointIds = getExportPointIds(renderState, disabledPointIds);
		const mirrorPairs = activeSourcePointIds.map((pointId) => ({
			source: pointId,
			mirror: renderState.mirrorByPointId.get(pointId) ?? pointId,
		}));
		const payload = {
			pointIds: activePointIds,
			sourceSide: side,
			sourcePointIds: activeSourcePointIds,
			mirrorPointIds: Array.from(new Set(mirrorPairs.map((pair) => pair.mirror).filter((pointId) => !activeSourcePointIds.includes(pointId)))).sort((a, b) => a - b),
			centerPointIds: activeSourcePointIds.filter((pointId) => renderState.centerPointIds.has(pointId)),
			disabledSourcePointIds: Array.from(disabledPointIds).sort((a, b) => a - b),
			mirrorPairs,
		};

		downloadJson('loading-mask-points.json', payload);
	};

	const handleUploadClick = () => {
		fileInputRef.current?.click();
	};

	const handleUploadChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		event.target.value = '';
		if (!file) return;

		if (!detectionModel || !isModelLoaded) {
			setUploadStatus(isModelLoading ? 'Chargement du modele...' : 'Modele indisponible');
			return;
		}

		try {
			setUploadStatus('Analyse...');
			const imageDataUrl = await readFileAsDataUrl(file);
			const image = await loadImage(imageDataUrl);
			const sourceCanvas = document.createElement('canvas');
			sourceCanvas.width = image.width;
			sourceCanvas.height = image.height;
			const sourceCtx = sourceCanvas.getContext('2d');
			if (!sourceCtx) throw new Error('Canvas unavailable');

			sourceCtx.drawImage(image, 0, 0, image.width, image.height);
			const result = detectionModel.detect(sourceCanvas);
			const landmarks = result.faceLandmarks?.[0];
			if (!landmarks?.length) {
				setUploadStatus('Aucun visage detecte');
				return;
			}

			const nextPredictions = {
				keypoints: landmarks.map((landmark) => [landmark.x, landmark.y, landmark.z ?? 0]),
				annotations: {} as Predictions['annotations'],
			};

			setUploadedImageUser(imageDataUrl);
			setUploadedPredictions(nextPredictions);
			setDisabledPointIds(new Set());
			setUploadStatus('');
			cacheDebugData(imageDataUrl, nextPredictions);
		} catch (error) {
			console.warn('[DebugLoadingMask]', 'Unable to process uploaded image', error);
			setUploadStatus('Upload impossible');
		}
	};

	if (!imageUser || !predictions) {
		return (
			<DebugEmptyStateWrapper>
				<DebugEmptyState>Open this page after taking a photo, or upload an image from desktop.</DebugEmptyState>
				<UploadInput ref={fileInputRef} type='file' accept='image/*' onChange={handleUploadChange} />
				<EmptyUploadButton onClick={handleUploadClick}>Upload image</EmptyUploadButton>
				{uploadStatus && <EmptyUploadStatus>{uploadStatus}</EmptyUploadStatus>}
			</DebugEmptyStateWrapper>
		);
	}

	const modelStatus = isModelLoading ? 'Modele...' : uploadStatus;

	return (
		<DebugContainer>
			<DebugCanvas ref={canvasRef} onClick={handleCanvasClick} onMouseMove={handleCanvasMouseMove} onMouseLeave={() => setHoveredPoint(null)} />
			{hoveredPoint && (
				<PointLabel style={{ left: hoveredPoint.x + 10, top: hoveredPoint.y - 10 }}>
					{hoveredPoint.pointId}
				</PointLabel>
			)}
			<DebugToolbar>
				<UploadInput ref={fileInputRef} type='file' accept='image/*' onChange={handleUploadChange} />
				<SideButton $active={side === 'left'} onClick={() => setSide('left')}>Gauche</SideButton>
				<SideButton $active={side === 'right'} onClick={() => setSide('right')}>Droite</SideButton>
				<SideButton $active={side === 'both'} onClick={() => setSide('both')}>Deux côtés</SideButton>
				<Counter>{displayCount}/{totalCount} affiches - {activeCount} export</Counter>
				<ToolbarButton onClick={handleUploadClick}>Upload</ToolbarButton>
				<SaveButton onClick={handleSave}>Enregistrer JSON</SaveButton>
				{modelStatus && <ToolbarStatus>{modelStatus}</ToolbarStatus>}
			</DebugToolbar>
		</DebugContainer>
	);
};

const readFileAsDataUrl = (file: File) => new Promise<string>((resolve, reject) => {
	const reader = new FileReader();
	reader.onload = () => resolve(String(reader.result));
	reader.onerror = reject;
	reader.readAsDataURL(file);
});

const loadImage = (src: string) => new Promise<HTMLImageElement>((resolve, reject) => {
	const image = new Image();
	image.onload = () => resolve(image);
	image.onerror = reject;
	image.src = src;
});

const cacheDebugData = (imageUser: string, predictions: Predictions) => {
	try {
		window.sessionStorage.setItem('debugLoadingMaskImage', imageUser);
		window.sessionStorage.setItem('debugLoadingMaskPredictions', JSON.stringify(predictions));
	} catch (error) {
		console.warn('[DebugLoadingMask]', 'Unable to cache uploaded image', error);
	}
};

const getCachedDebugData = () => {
	try {
		const imageUser = window.sessionStorage.getItem('debugLoadingMaskImage');
		const predictions = window.sessionStorage.getItem('debugLoadingMaskPredictions');
		return {
			imageUser,
			predictions: predictions ? JSON.parse(predictions) : null,
		};
	} catch (error) {
		console.warn('[DebugLoadingMask]', 'Unable to read cached debug data', error);
		return { imageUser: null, predictions: null };
	}
};

const getCenterX = (points: MeshPoint[]) => {
	const centerPoints = CENTER_AXIS_POINT_IDS.map((pointId) => points[pointId]).filter(Boolean);
	if (centerPoints.length === 0) {
		return points.reduce((sum, point) => sum + point.x, 0) / Math.max(points.length, 1);
	}

	return centerPoints.reduce((sum, point) => sum + point.x, 0) / centerPoints.length;
};

const getCenterPointIds = (points: MeshPoint[]) => new Set(CENTER_AXIS_POINT_IDS.filter((pointId) => points[pointId]));

const getSidePointIds = (points: MeshPoint[], centerX: number, centerPointIds: Set<number>, side: DebugSide) => (
	points
		.map((point, pointId) => ({ point, pointId }))
		.filter(({ point, pointId }) => side === 'both' || centerPointIds.has(pointId) || (side === 'left' ? point.x < centerX : point.x > centerX))
		.map(({ pointId }) => pointId)
);

const buildMirrorMap = (points: MeshPoint[], displayPointIds: number[], centerX: number, centerPointIds: Set<number>, side: DebugSide) => {
	const mirrorByPointId = new Map<number, number>();
	if (side === 'both') {
		displayPointIds.forEach((pointId) => mirrorByPointId.set(pointId, pointId));
		return mirrorByPointId;
	}

	const candidates = points
		.map((point, pointId) => ({ point, pointId }))
		.filter(({ point, pointId }) => !centerPointIds.has(pointId) && (side === 'left' ? point.x > centerX : point.x < centerX));

	displayPointIds.forEach((pointId) => {
		const point = points[pointId];
		if (!point || centerPointIds.has(pointId)) {
			mirrorByPointId.set(pointId, pointId);
			return;
		}

		const targetX = 2 * centerX - point.x;
		const mirror = candidates.reduce<{ pointId: number; distance: number } | null>((best, candidate) => {
			const dx = candidate.point.x - targetX;
			const dy = candidate.point.y - point.y;
			const distance = dx * dx + dy * dy;
			return !best || distance < best.distance ? { pointId: candidate.pointId, distance } : best;
		}, null);

		mirrorByPointId.set(pointId, mirror?.pointId ?? pointId);
	});

	return mirrorByPointId;
};

const buildDebugEdges = (points: MeshPoint[], displayPointIds: number[]) => {
	const edges = buildClosestPointEdges(points, displayPointIds);
	const seen = new Set(edges.map(getEdgeKey));
	const displayPointSet = new Set(displayPointIds);

	for (let index = 1; index < FACE_OVAL_POINT_IDS.length; index++) {
		const start = FACE_OVAL_POINT_IDS[index - 1];
		const end = FACE_OVAL_POINT_IDS[index];
		if (!displayPointSet.has(start) || !displayPointSet.has(end) || !points[start] || !points[end]) continue;

		const key = getEdgeKey([start, end]);
		if (!seen.has(key)) {
			seen.add(key);
			edges.push([start, end]);
		}
	}

	return edges;
};

const getEdgeKey = ([start, end]: MeshEdge) => start < end ? `${start}-${end}` : `${end}-${start}`;

const getClosestDisplayPointId = (renderState: DebugRenderState, x: number, y: number) => {
	const closest = renderState.displayPointIds.reduce<{ pointId: number; distance: number } | null>((best, pointId) => {
		const point = renderState.points[pointId];
		if (!point) return best;

		const dx = point.x - x;
		const dy = point.y - y;
		const distance = Math.sqrt(dx * dx + dy * dy);
		return !best || distance < best.distance ? { pointId, distance } : best;
	}, null);

	return closest && closest.distance <= CLICK_RADIUS ? closest.pointId : null;
};

const getExportPointIds = (renderState: DebugRenderState, disabledPointIds: Set<number>) => {
	const activePointIds = new Set<number>();
	renderState.displayPointIds.forEach((pointId) => {
		if (disabledPointIds.has(pointId)) return;
		activePointIds.add(pointId);
		activePointIds.add(renderState.mirrorByPointId.get(pointId) ?? pointId);
	});

	return Array.from(activePointIds).sort((a, b) => a - b);
};

const drawMask = (ctx: CanvasRenderingContext2D, renderState: DebugRenderState, disabledPointIds: Set<number>) => {
	const { displayPointIds, edges, points } = renderState;

	ctx.lineCap = 'round';
	ctx.lineJoin = 'round';

	edges.forEach(([start, end]) => {
		const startPoint = points[start];
		const endPoint = points[end];
		if (!startPoint || !endPoint) return;

		const disabled = disabledPointIds.has(start) || disabledPointIds.has(end);
		const opacity = disabled ? 0.15 : 0.78;

		ctx.beginPath();
		ctx.moveTo(startPoint.x, startPoint.y);
		ctx.lineTo(endPoint.x, endPoint.y);
		ctx.strokeStyle = `rgba(255, 255, 255, ${opacity})`;
		ctx.lineWidth = disabled ? 0.8 : 1;
		ctx.stroke();
	});

	displayPointIds.forEach((pointId) => {
		const point = points[pointId];
		if (!point) return;

		const disabled = disabledPointIds.has(pointId);
		const opacity = disabled ? 0.15 : 0.95;

		ctx.beginPath();
		ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
		ctx.arc(point.x, point.y, disabled ? 2.5 : 3, 0, 2 * Math.PI);
		ctx.fill();

		ctx.beginPath();
		ctx.strokeStyle = `rgba(0, 0, 0, ${disabled ? 0.15 : 0.45})`;
		ctx.lineWidth = 1;
		ctx.arc(point.x, point.y, disabled ? 2.5 : 3, 0, 2 * Math.PI);
		ctx.stroke();
	});
};

const downloadJson = (fileName: string, payload: unknown) => {
	const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = fileName;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
};

const DebugContainer = styled.main`
	position: fixed;
	inset: 0;
	background: #000;
`;

const DebugCanvas = styled.canvas`
	position: fixed;
	inset: 0;
	display: block;
	width: 100vw;
	height: 100vh;
	background: #000;
	cursor: crosshair;
`;

const PointLabel = styled.div`
	position: fixed;
	z-index: 3;
	padding: 3px 6px;
	border: 1px solid rgba(255, 255, 255, 0.5);
	background: rgba(0, 0, 0, 0.76);
	color: #fff;
	font-family: Arial, Helvetica, sans-serif;
	font-size: 12px;
	line-height: 1;
	pointer-events: none;
`;

const DebugToolbar = styled.div`
	position: fixed;
	right: 16px;
	bottom: 16px;
	z-index: 2;
	display: flex;
	align-items: center;
	gap: 8px;
	box-sizing: border-box;
	padding: 8px;
	border: 1px solid rgba(255, 255, 255, 0.18);
	background: rgba(12, 12, 12, 0.78);
	backdrop-filter: blur(10px);
	font-family: Arial, Helvetica, sans-serif;
`;

const UploadInput = styled.input`
	display: none;
`;

const SideButton = styled.button<{ $active: boolean }>`
	min-width: 64px;
	height: 34px;
	border: 1px solid ${(props) => props.$active ? '#fff' : 'rgba(255, 255, 255, 0.25)'};
	background: ${(props) => props.$active ? '#fff' : 'transparent'};
	color: ${(props) => props.$active ? '#111' : '#fff'};
	font-size: 13px;
	cursor: pointer;
`;

const Counter = styled.span`
	min-width: 156px;
	color: #fff;
	font-size: 13px;
	text-align: center;
`;

const ToolbarButton = styled.button`
	height: 34px;
	border: 1px solid rgba(255, 255, 255, 0.35);
	background: transparent;
	color: #fff;
	font-size: 13px;
	cursor: pointer;
`;

const SaveButton = styled(ToolbarButton)`
	border-color: #fff;
`;

const ToolbarStatus = styled.span`
	color: #fff;
	font-size: 12px;
`;

const DebugEmptyStateWrapper = styled.main`
	position: fixed;
	inset: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 14px;
	box-sizing: border-box;
	padding: 24px;
	background: #101010;
	color: #fff;
	font-family: Arial, Helvetica, sans-serif;
`;

const DebugEmptyState = styled.p`
	max-width: 360px;
	margin: 0;
	font-size: 15px;
	line-height: 1.4;
	text-align: center;
`;

const EmptyUploadButton = styled(ToolbarButton)`
	height: 38px;
	padding: 0 18px;
`;

const EmptyUploadStatus = styled.span`
	font-size: 13px;
`;

export default DebugLoadingMask;
